import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import '../styles/Article.css';
import '../styles/articles.css';
import '../styles/lander.css';

const VideoLockOverlay = ({ title, videoId, onUnlock, password }) => {
  return (
    <div className="video-container locked">
      <div className="video-lock-overlay">
        <h3>{title}</h3>
        <button onClick={() => onUnlock(videoId)}>
          🔒 Unlock Video - {password}
        </button>
        <p className="lock-info">
          It's only to prevent automatic website copiers. Enter the password from button to unlock video playback
        </p>
      </div>
    </div>
  );
};

const PasswordModal = ({ isOpen, onClose, onSubmit, videoTitle }) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(password);
    setPassword('');
  };

  if (!isOpen) return null;

  return (
    <div className="password-modal-overlay">
      <div className="password-modal">
        <h3>Unlock Video</h3>
        <p className="video-title">{videoTitle}</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoFocus
          />
          <div className="modal-buttons">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="submit-button">
              Unlock
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const VideoPlayer = ({ videoId, videoLink }) => {
  if (!videoLink) return null;

  return (
    <div className="video-container">
      <iframe
        src={`https://streamable.com/e/${videoLink}`}
        title={`Video ${videoId}`}
        frameBorder="0"
        width="100%"
        height="100%"
        allowFullScreen
      />
    </div>
  );
};

const Article = ({ articlePath }) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [videos, setVideos] = useState({});
  const [videoPasswords, setVideoPasswords] = useState({});
  const [unlockedVideos, setUnlockedVideos] = useState(new Set());
  const playerRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [tableOfContents, setTableOfContents] = useState([]);

  // Function to extract headers and create TOC
  const generateTableOfContents = (content) => {
    const headers = [];
    const lines = content.split('\n');
    
    lines.forEach(line => {
      // Look for both markdown headers and HTML headers
      const markdownMatch = line.match(/^(#{1,3})\s+(.+)/);
      const htmlMatch = line.match(/<h([1-3])[^>]*>([^<]+)<\/h\1>/);
      
      if (markdownMatch) {
        const level = markdownMatch[1].length;
        const text = markdownMatch[2].trim();
        const id = text
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s]+/g, '-');
        
        headers.push({ level, text, id });
      } else if (htmlMatch) {
        const level = parseInt(htmlMatch[1]);
        const text = htmlMatch[2].trim();
        const id = text
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s]+/g, '-');
        
        headers.push({ level, text, id });
      }
    });

    return headers;
  };

  // Function to generate random password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Load videos data and generate passwords
  useEffect(() => {
    fetch('/data/videos.json')
      .then(res => res.json())
      .then(videosData => {
        const videosMap = {};
        const passwordsMap = {};
        videosData.videos.forEach(video => {
          videosMap[video.id] = video;
          passwordsMap[video.id] = generatePassword();
        });
        setVideos(videosMap);
        setVideoPasswords(passwordsMap);
      })
      .catch(error => console.error('Error loading data:', error));
  }, []);

  const handleUnlockVideo = (videoId) => {
    setCurrentVideo(videoId);
    setModalOpen(true);
  };

  const handlePasswordSubmit = (password) => {
    if (password === videoPasswords[currentVideo]) {
      setUnlockedVideos(prev => new Set([...prev, currentVideo]));
      setModalOpen(false);
    } else {
      alert('Incorrect password');
    }
  };

  const handleTocClick = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(id);
    
    if (element) {
      const headerOffset = 100;
      
      try {
        // Calculate the final position
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset;

        // Scroll in one smooth motion
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });

        // Visual feedback
        requestAnimationFrame(() => {
          element.style.transition = 'background-color 0.5s ease';
          element.style.backgroundColor = 'rgba(52, 152, 219, 0.2)';
          
          setTimeout(() => {
            element.style.backgroundColor = 'transparent';
          }, 1500);
        });
      } catch (error) {
        console.error('Error during scrolling');
      }
    }
  };

  const processContent = (text) => {
    if (!text.includes('<video id="')) {
      return (
        <div className="article-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({node, children, ...props}) => {
                const headerText = Array.isArray(children) ? children.join('') : children.toString();
                const id = headerText
                  .toLowerCase()
                  .replace(/[^\w\s-]/g, '')
                  .replace(/[\s]+/g, '-');
                return <h1 id={id} {...props}>{children}</h1>;
              },
              h2: ({node, children, ...props}) => {
                const headerText = Array.isArray(children) ? children.join('') : children.toString();
                const id = headerText
                  .toLowerCase()
                  .replace(/[^\w\s-]/g, '')
                  .replace(/[\s]+/g, '-');
                return <h2 id={id} {...props}>{children}</h2>;
              },
              h3: ({node, children, ...props}) => {
                const headerText = Array.isArray(children) ? children.join('') : children.toString();
                const id = headerText
                  .toLowerCase()
                  .replace(/[^\w\s-]/g, '')
                  .replace(/[\s]+/g, '-');
                return <h3 id={id} {...props}>{children}</h3>;
              },
              img: ({node, ...props}) => <img className="article-image" {...props} alt={props.alt || ''} />,
              table: ({node, ...props}) => <div className="table-container"><table {...props} /></div>
            }}
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }

    const parts = text.split(/<video id="([^"]+)">/);
    return (
      <div className="article-content">
        {parts.map((part, index) => {
          if (index % 2 === 0) {
            return (
              <ReactMarkdown
                key={index}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({node, children, ...props}) => {
                    const headerText = Array.isArray(children) ? children.join('') : children.toString();
                    const id = headerText
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/[\s]+/g, '-');
                    return <h1 id={id} {...props}>{children}</h1>;
                  },
                  h2: ({node, children, ...props}) => {
                    const headerText = Array.isArray(children) ? children.join('') : children.toString();
                    const id = headerText
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/[\s]+/g, '-');
                    return <h2 id={id} {...props}>{children}</h2>;
                  },
                  h3: ({node, children, ...props}) => {
                    const headerText = Array.isArray(children) ? children.join('') : children.toString();
                    const id = headerText
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/[\s]+/g, '-');
                    return <h3 id={id} {...props}>{children}</h3>;
                  },
                  img: ({node, ...props}) => <img className="article-image" {...props} alt={props.alt || ''} />,
                  table: ({node, ...props}) => <div className="table-container"><table {...props} /></div>
                }}
              >
                {part}
              </ReactMarkdown>
            );
          }

          const videoId = part;
          const video = videos[videoId];
          
          if (!video) return null;

          if (!unlockedVideos.has(videoId)) {
            return <VideoLockOverlay 
              key={videoId}
              title={video.title}
              videoId={videoId}
              onUnlock={handleUnlockVideo}
              password={videoPasswords[videoId]}
            />;
          }

          return (
            <div key={videoId} className="video-container">
              <VideoPlayer videoId={videoId} videoLink={video.link} />
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    const loadContent = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(articlePath);
        if (!response.ok) {
          throw new Error(`Failed to load content: ${response.status} ${response.statusText}`);
        }
        const text = await response.text();
        setContent(text);
        setTableOfContents(generateTableOfContents(text));
      } catch (error) {
        console.error('Error loading article:', error);
        setContent('Error loading content. Please try again later.');
      } finally {
        setIsLoading(false);
        setTimeout(() => setIsVisible(true), 100);
      }
    };

    loadContent();
  }, [articlePath]);

  // Add CSS class based on header level
  const getTocItemClass = (level) => {
    switch(level) {
      case 1: return 'toc-h1';
      case 2: return 'toc-h2';
      case 3: return 'toc-h3';
      default: return '';
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          {!articlePath.includes('lander.md') && (
            <div className="table-of-contents">
              <h3>Topics in this lesson</h3>
              <ul>
                {tableOfContents.map((item, index) => (
                  <li 
                    key={index} 
                    className={getTocItemClass(item.level)}
                    onClick={(e) => handleTocClick(e, item.id)}
                  >
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {processContent(content)}
          <PasswordModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            onSubmit={handlePasswordSubmit}
            videoTitle={videos[currentVideo]?.title}
          />
        </>
      )}
    </>
  );
};

export default Article; 